import React, { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  Clock,
  History,
  Info,
  Loader2,
  RefreshCw,
  User,
  Zap,
} from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useForecastRefresh } from '@/contexts/ForecastRefreshContext';
import { useFormatting } from '@/hooks/useFormatting';
import { triggerForecastRun } from '@/api/services/forecastService';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

// ─── Demo seed data ────────────────────────────────────────────────────────────

interface RefreshHistoryEntry {
  id: string;
  dateTime: Date;
  type: 'Scheduled' | 'Manual';
  triggeredBy: string;
  durationMs: number;
  notes?: string;
}

function buildSeedHistory(): RefreshHistoryEntry[] {
  const now = new Date();
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);

  const d = (daysAgo: number, hour: number, minute = 0): Date => {
    const dt = new Date(startOfToday);
    dt.setDate(dt.getDate() - daysAgo);
    dt.setHours(hour, minute, 0, 0);
    return dt;
  };

  return [
    {
      id: 'rh-1',
      dateTime: d(0, 6, 0),
      type: 'Scheduled',
      triggeredBy: 'System',
      durationMs: 222_000, // 3 min 42 sec
      notes: undefined,
    },
    {
      id: 'rh-2',
      dateTime: d(1, 14, 23),
      type: 'Manual',
      triggeredBy: 'Sarah Mitchell',
      durationMs: 247_000, // 4 min 7 sec
      notes: '1 data source returned incomplete data — forecast recalculated with available data',
    },
    {
      id: 'rh-3',
      dateTime: d(2, 6, 0),
      type: 'Scheduled',
      triggeredBy: 'System',
      durationMs: 218_000, // 3 min 38 sec
      notes: undefined,
    },
    {
      id: 'rh-4',
      dateTime: d(3, 6, 0),
      type: 'Scheduled',
      triggeredBy: 'System',
      durationMs: 231_000, // 3 min 51 sec
      notes: undefined,
    },
  ];
}

const SEED_HISTORY = buildSeedHistory();

// The last scheduled refresh is SEED_HISTORY[0]
const LAST_REFRESH = SEED_HISTORY[0];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDuration(ms: number): string {
  const totalSec = Math.round(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min === 0) return `${sec} sec`;
  return `${min} min ${sec} sec`;
}

function timeAgoLabel(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  if (diffHours > 0) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffMin > 0) return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
  return 'just now';
}

function formatDateTime(date: Date): string {
  const today = new Date();
  const isToday =
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  const timeStr = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  if (isToday) return `Today at ${timeStr}`;

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();

  if (isYesterday) return `Yesterday at ${timeStr}`;

  return `${date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} at ${timeStr}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const ForecastRefreshPage: React.FC = () => {
  const { currentUser, mode } = useApp();
  const { hasPermission } = usePermissions();
  const { refreshState, startRefresh, completeRefresh } = useForecastRefresh();

  if (!hasPermission('trigger_forecast_refresh')) return null;
  const { formatCurrency: _fc } = useFormatting();

  const [acknowledged, setAcknowledged] = useState(false);
  const [history, setHistory] = useState<RefreshHistoryEntry[]>(SEED_HISTORY);

  const simulationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (simulationTimer.current) {
        clearTimeout(simulationTimer.current);
      }
    };
  }, []);

  const isInProgress = refreshState.status === 'in-progress';
  const isComplete = refreshState.status === 'complete';

  // Estimated duration: last refresh duration ± 20–30 second variance
  const estimatedDurationMs = LAST_REFRESH.durationMs + (Math.random() * 20_000 - 10_000);

  const handleStartRefresh = async () => {
    if (!acknowledged || isInProgress) return;

    if (mode === 'live') {
      try {
        await triggerForecastRun();
        startRefresh(currentUser.name, estimatedDurationMs);
      } catch {
        // Polling will detect state changes; no-op on trigger failure
      }
      return;
    }

    // Demo mode: simulate completion after estimated duration
    if (simulationTimer.current) {
      clearTimeout(simulationTimer.current);
    }

    startRefresh(currentUser.name, estimatedDurationMs);

    simulationTimer.current = setTimeout(() => {
      completeRefresh();
      const newEntry: RefreshHistoryEntry = {
        id: `rh-manual-${Date.now()}`,
        dateTime: new Date(),
        type: 'Manual',
        triggeredBy: currentUser.name,
        durationMs: Math.round(estimatedDurationMs),
      };
      setHistory((prev) => [newEntry, ...prev]);
      simulationTimer.current = null;
    }, estimatedDurationMs);
  };

  // Progress calculation (0–100%)
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (!isInProgress || !refreshState.startTime) {
      setProgress(0);
      return;
    }
    const interval = setInterval(() => {
      const elapsed = Date.now() - refreshState.startTime!.getTime();
      const pct = Math.min(95, (elapsed / refreshState.estimatedDurationMs) * 100);
      setProgress(Math.round(pct));
    }, 500);
    return () => clearInterval(interval);
  }, [isInProgress, refreshState.startTime, refreshState.estimatedDurationMs]);

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Page heading */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Refresh Forecast</h1>
        <p className="text-gray-500 mt-1 text-sm">
          Manually trigger a full forecast recalculation outside of the scheduled sync.
        </p>
      </div>

      {/* In-progress / complete status alert */}
      {isInProgress && (
        <Alert className="border-amber-300 bg-amber-50">
          <Loader2 className="h-4 w-4 text-amber-600 animate-spin" />
          <AlertDescription className="text-amber-800 font-medium">
            Forecast refresh in progress — {progress}% complete. Recurring transactions, data source
            configuration, and scenario modifications are temporarily locked.
          </AlertDescription>
        </Alert>
      )}
      {isComplete && (
        <Alert className="border-green-300 bg-green-50">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800 font-medium">
            Forecast refresh complete. New forecast is now live.
          </AlertDescription>
        </Alert>
      )}

      {/* Zone 1 — Current Status */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="h-4 w-4 text-teal-600" />
            Current Status
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm">
          <div>
            <p className="text-gray-500 text-xs uppercase tracking-wide mb-0.5">Last refresh</p>
            <p className="font-medium text-gray-900">
              {formatDateTime(LAST_REFRESH.dateTime)}
              <span className="text-gray-400 font-normal ml-1">· {timeAgoLabel(LAST_REFRESH.dateTime)}</span>
            </p>
          </div>
          <div>
            <p className="text-gray-500 text-xs uppercase tracking-wide mb-0.5">Duration</p>
            <p className="font-medium text-gray-900">{formatDuration(LAST_REFRESH.durationMs)}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs uppercase tracking-wide mb-0.5">Triggered by</p>
            <p className="font-medium text-gray-900">{LAST_REFRESH.triggeredBy}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs uppercase tracking-wide mb-0.5">Forecast age</p>
            <p className="font-medium text-gray-900">{timeAgoLabel(LAST_REFRESH.dateTime)} old</p>
          </div>
        </CardContent>
      </Card>

      {/* Zone 2 — What to Expect */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Info className="h-4 w-4 text-teal-600" />
            What to Expect
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-gray-700">
          <div className="grid grid-cols-2 gap-x-8 gap-y-3">
            <div>
              <p className="text-gray-500 text-xs uppercase tracking-wide mb-0.5">Estimated duration</p>
              <p className="font-medium text-gray-900">{formatDuration(estimatedDurationMs)} (based on last refresh)</p>
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <p className="font-medium text-gray-900">What happens during a refresh</p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li>The forecast recalculates from all connected data sources.</li>
              <li>Scenario impact values are recalculated against the updated base forecast.</li>
              <li>AI recommendations are refreshed based on the latest data.</li>
            </ul>
          </div>

          <div className="space-y-2">
            <p className="font-medium text-gray-900">What is temporarily locked during a refresh</p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li>Recurring transactions (read-only during refresh)</li>
              <li>Data source configuration</li>
              <li>Scenario activation and modification</li>
            </ul>
            <p className="text-gray-500 text-xs">
              Normal activity continues for everything else — actions, comments, preferences, reports, and admin configuration.
            </p>
          </div>

          <div className="flex gap-3 rounded-lg bg-blue-50 p-4 text-sm text-blue-900 border border-blue-100">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 text-blue-600 mt-0.5" />
            <p>
              If you find yourself triggering manual refreshes regularly, consider adjusting your scheduled
              refresh frequency in <span className="font-medium">Admin → General Settings</span>.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Trigger section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-teal-600" />
            Start Refresh
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id="acknowledge"
              checked={acknowledged}
              onCheckedChange={(v) => setAcknowledged(v === true)}
              disabled={isInProgress}
            />
            <Label
              htmlFor="acknowledge"
              className="text-sm text-gray-700 cursor-pointer leading-snug"
            >
              I understand this will temporarily lock recurring transactions, data source configuration,
              and scenario modifications while the refresh runs.
            </Label>
          </div>

          <Button
            onClick={handleStartRefresh}
            disabled={!acknowledged || isInProgress}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            {isInProgress ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Refresh in progress ({progress}%)…
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Start Forecast Refresh
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Zone 3 — Refresh History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <History className="h-4 w-4 text-teal-600" />
            Refresh History
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date / Time</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Triggered By</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="whitespace-nowrap text-sm">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                      {formatDateTime(entry.dateTime)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={entry.type === 'Scheduled' ? 'secondary' : 'outline'}
                      className="text-xs"
                    >
                      {entry.type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    <div className="flex items-center gap-1.5">
                      <User className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                      {entry.triggeredBy}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    <div className="flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                      {formatDuration(entry.durationMs)}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-gray-500 max-w-xs">
                    {entry.notes ? (
                      <span className="flex items-start gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                        {entry.notes}
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
